import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import Navbar from '../Layout/Navbar'
import { AppDispatch, RootState } from '@/redux/store'
import { useDispatch, useSelector } from 'react-redux'
import OGEAvatar from '../OGE/OGEAvatar'
import ChangePassword from './ChangePassword'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../ui/button'
import { updateProfileData } from '@/redux/asyncThunks/user'
import { SERVER_URL } from '@/api/config'
import { toast } from 'sonner'
import { fetchRooms, getRoomDetails } from '@/redux/asyncThunks/room'
import LLMApiKeysSection from '@/components/settings/LLMApiKeysSection'
import { SciteConnectionPanel } from '@/components/settings/SciteConnectionPanel'
import { GameRoomStatus } from '@/redux/types/room'
import { UserRole } from '@/constants/room'

const ProfileView: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()

  const user = useSelector((state: RootState) => state.user.user)
  const isProfessor = user?.role === UserRole.Professor
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const rooms = useSelector((state: RootState) => state.room.rooms)
  const roomsLoading = useSelector((state: RootState) => state.room.isLoading)
  const currentRoom = useSelector((state: RootState) => state.room.currentRoom)

  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const llmApiKeysSectionRef = useRef<HTMLDivElement | null>(null)
  const [settings, setSettings] = useState<{
    first_name: string
    last_name: string
    profile_picture: string | undefined
  }>({
    first_name: '',
    last_name: '',
    profile_picture: undefined,
  })
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (user?.name) {
      const nameArray = user.name.split(' ')
      const first_name = nameArray[0]
      const last_name = nameArray.length > 1 ? nameArray[1] : ''
      setSettings({
        first_name,
        last_name,
        profile_picture: user.profile_picture,
      })
    }
  }, [user])

  useEffect(() => {
    if (isProfessor) {
      dispatch(fetchRooms())
    }
  }, [dispatch, isProfessor])

  useEffect(() => {
    if (isProfessor && selectedRoomId) {
      dispatch(getRoomDetails(selectedRoomId))
    }
  }, [dispatch, selectedRoomId, isProfessor])

  useEffect(() => {
    if (!isProfessor || !selectedRoomId) return
    llmApiKeysSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }, [isProfessor, selectedRoomId, currentRoom?.roomid])

  const updateProfileHandler = async (formData: FormData) => {
    try {
      const resultAction = await dispatch(updateProfileData(formData))
      if (updateProfileData.rejected.match(resultAction)) {
        setIsSubmitting(false)
        return
      }
      toast.success('Profile saved successfully')
      setIsSubmitting(false)
    } catch (error) {
      toast.error('Error updating profile')
      setIsSubmitting(false)
    }
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSubmitting(true)
    const selectedFile = event.target.files ? event.target.files[0] : null
    if (selectedFile) {
      const formData = new FormData()
      formData.append('profile_picture', selectedFile)
      updateProfileHandler(formData)
    }
  }

  const handleProfileUpdate = ({
    first_name,
    last_name,
  }: {
    first_name: string
    last_name: string
  }) => {
    setIsSubmitting(true)
    const formData = new FormData()
    formData.append('first_name', first_name)
    formData.append('last_name', last_name)
    updateProfileHandler(formData)
  }

  return (
    <div className='flex h-screen w-screen flex-col'>
      <Navbar />
      <div className='flex flex-1 flex-col px-24'>
        <div className='flex w-3/4 flex-col md:w-full'>
          <Card className='w-full max-w-md border-none shadow-none'>
            <CardHeader className='space-y-3 flex flex-col px-0 pt-0'>
              <CardTitle className='text-2xl text-[#023572] font-bold'>
                Profile
              </CardTitle>
              <CardDescription className='text-base text-black font-normal'>
                Update your profile information.
              </CardDescription>
            </CardHeader>
            <CardContent className='px-0'>
              <div className='relative w-fit'>
                <OGEAvatar
                  src={
                    !settings?.profile_picture
                      ? undefined
                      : settings.profile_picture.startsWith('http')
                        ? settings.profile_picture
                        : SERVER_URL + settings.profile_picture
                  }
                  fallback={
                    user?.name?.substring(0, 2).toUpperCase() ||
                    user?.email?.substring(0, 2).toUpperCase()
                  }
                  variant='lg'
                  isLoading={isLoading}
                />
                <label
                  htmlFor='file-input'
                  className='absolute bottom-0 right-0 p-1 bg-white rounded-full shadow-md cursor-pointer'
                >
                  <img
                    src='/icons/pencil.svg'
                    alt='Edit'
                    className='h-2.5 w-2.5'
                  />
                </label>

                <input
                  id='file-input'
                  type='file'
                  className='hidden'
                  accept='image/*'
                  onChange={handleImageChange}
                />
              </div>
              <div className='flex flex-1'>
                <div className='space-y-2 mt-2  me-2 flex w-1/2 flex-col'>
                  <Label className='text-base font-normal' htmlFor='name'>
                    First Name
                  </Label>
                  <div className='relative'>
                    <Input
                      className='h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF]'
                      id='name'
                      name='name'
                      placeholder='At least 8 characters'
                      value={settings.first_name}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          first_name: event.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className='space-y-2 mt-2 flex w-1/2 flex-col'>
                  <Label className='text-base font-normal' htmlFor='name'>
                    Last Name
                  </Label>
                  <div className='relative'>
                    <Input
                      className='h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF]'
                      id='name'
                      name='name'
                      placeholder='At least 8 characters'
                      value={settings.last_name}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          last_name: event.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className='space-y-2 mt-3'>
                <Label className='text-base font-normal' htmlFor='email'>
                  Email
                </Label>
                <div className='relative'>
                  <Input
                    disabled
                    className='h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF]'
                    id='email'
                    name='email'
                    placeholder='Enter your email address'
                    value={user?.email}
                  />
                </div>
              </div>
              <Button
                type='submit'
                disabled={isSubmitting || !settings.first_name}
                className='h-[38px] text-base font-normal bg-dark-blue hover:bg-dark-blue-hovered mt-3'
                onClick={() => {
                  if (settings.first_name) {
                    handleProfileUpdate({
                      first_name: settings.first_name,
                      last_name: settings.last_name,
                    })
                  }
                }}
              >
                Update
              </Button>
            </CardContent>
          </Card>
        </div>
        {/* Change Password Section  */}
        <ChangePassword />
        {isProfessor && (
          <div className='flex w-3/4 flex-col md:w-full'>
            <Card className='w-full max-w-md border-none shadow-none'>
              <CardHeader className='space-y-3 flex flex-col px-0 pt-0'>
                <CardTitle className='text-lg text-[#023572] font-bold'>
                  Game LLM API keys
                </CardTitle>
                <CardDescription className='text-base text-black font-normal'>
                  Set per-game API keys for OpenAI and Claude. All LLM usage in a
                  game will use that game&apos;s keys when set. Keys are never
                  displayed after saving.
                </CardDescription>
              </CardHeader>
              <CardContent className='px-0 space-y-4'>
                {roomsLoading && rooms.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>
                    Loading your games...
                  </p>
                ) : rooms.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>
                    No games available to configure. Create a game from Rooms
                    first.
                  </p>
                ) : (
                  <>
                    <div className='space-y-2'>
                      <Label
                        htmlFor='game-llm-select'
                        className='text-base font-normal'
                      >
                        Select a game
                      </Label>
                      <Select
                        value={selectedRoomId ?? ''}
                        onValueChange={(value) =>
                          setSelectedRoomId(value || null)
                        }
                      >
                        <SelectTrigger
                          id='game-llm-select'
                          className='h-10 bg-[#FAFCFF]'
                        >
                          <SelectValue placeholder='Choose a game' />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.map((room) => (
                            <SelectItem
                              key={room.roomid}
                              value={room.roomid}
                            >
                              {room.roomname ||
                                room.scenario_title ||
                                room.roomid}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div ref={llmApiKeysSectionRef}>
                      {!selectedRoomId ? (
                        <p className='text-sm text-muted-foreground'>
                          Select a game above to set or update LLM API keys for
                          that game. All LLMs in the game will use these keys
                          when set.
                        </p>
                      ) : roomsLoading ||
                        currentRoom?.roomid !== selectedRoomId ? (
                        <p className='text-sm text-muted-foreground'>
                          Loading game details...
                        </p>
                      ) : (
                        <>
                          <LLMApiKeysSection
                            roomId={selectedRoomId}
                            disabled={
                              currentRoom?.status !== GameRoomStatus.NOT_STARTED
                            }
                          />
                        </>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

          <Card className='w-full max-w-md border-none shadow-none'>
            <CardHeader className='space-y-3 flex flex-col px-0 pt-0'>
              <CardTitle className='text-lg text-[#023572] font-bold'>
                MCP Service Integration
              </CardTitle>
              <CardDescription className='text-base text-black font-normal'>
                Connect third-party MCP services. Once connected, the chatbots
                can search live scientific literature across all rooms.
              </CardDescription>
            </CardHeader>
            <CardContent className='px-0'>
              <SciteConnectionPanel />
            </CardContent>
          </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfileView
